/* eslint-disable import/no-cycle */
import { notification } from 'antd';
import { AxiosResponse } from 'axios';
import history from 'constants/history';
import { get } from 'lodash';
import moment from 'moment';
import ChatMessagesAction from 'redux/chat-messages/chat-messages.action';
import UserAction from 'redux/user/user.actions';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import ROUTES from 'routes/routeConstants';
import AuthService from 'services/api/auth.service';
import UserService from 'services/api/user.service';
// import apolloClient from 'services/graphql/config';
// import { GET_SINGLE_USER } from 'services/graphql/queries/user.query';
import { CallBack, ICommonCallbackPayload } from 'types/common';
import { LogoutPayload, TokenType, UserPayload } from 'types/user';
import { convertExpireTime } from 'utils/utils';

import { StandardAction } from '../types';
import AuthAction from './auth.actions';
import AuthSelectors from './auth.selectors';

const {
  userSignInSuccess,
  userSignOutSuccess,
  userSignUpSuccess,
  userSignUpError,
  userSignInFailure,
  userResetPasswordSuccess,
  userResetPasswordError,
  verifyTokenSuccess,
  verifyTokenFailure,
  getNewAccessTokenEnd,
} = AuthAction;

const { clearUser } = UserAction;
const { SIGNIN, SIGNOUT, SIGNUP, RESET_PASSWORD, VERIFY_TOKEN, GET_NEW_ACCESS_TOKEN } = AuthAction.TYPES;

function* createUserWithEmailPassword(action: StandardAction<UserPayload>): Generator<any, any, any> {
  const { payload } = action;

  if (payload) {
    const { email, password, callback } = payload;

    try {
      const result = yield call(UserService.update, { email }, { password });

      if (result.data) {
        yield put(userSignUpSuccess(result.data));

        callback?.callbackOnSuccess?.();
      }

      if (result.errors) {
        throw new Error(result.errors[0]);
      }
    } catch (error) {
      yield put(userSignUpError(error));
      notification.error({
        message: error.message,
      });

      callback?.callbackOnFailure?.(error);
    }
  }
}

function* signInUserWithEmailPassword(action: StandardAction<UserPayload>): Generator<any, any, any> {
  const { payload } = action;

  if (payload) {
    const { email, password, callback } = payload;

    try {
      const response = yield call(AuthService.signInWithEmailPassword, { email, password });
      const { accessToken, expiresIn } = get(response, 'data.token', {});
      const user = get(response, 'data.user', {});
      let expires = 0;
      if (expiresIn) {
        expires = Date.now() + parseFloat(expiresIn) * 1000;
      }

      yield put(UserAction.fetchUserSuccess(user));
      yield put(userSignInSuccess(accessToken, expires));
      callback?.callbackOnSuccess?.(user);
    } catch (error) {
      console.log('ERROR', error);
      yield put(userSignInFailure(error));
      callback?.callbackOnFailure?.(error);
    }
  }
}

function* signOut(action: StandardAction<LogoutPayload>) {
  const { payload } = action || {};

  try {
    // const signOutUser = yield call(APIService.post, '/auth/logout');
    const signOutUser = undefined;
    if (signOutUser === undefined) {
      const search = history?.location?.search || '';
      history.replace(payload?.redirectUrl || `${ROUTES.SIGN_IN}${search}`);
      yield put(userSignOutSuccess(signOutUser));
      yield put(clearUser());
      yield put(ChatMessagesAction.resetChatMessages());
      yield call(AuthService.logout);
    } else {
      // notification.error({
      //   message: signOutUser.message,
      // });
    }
  } catch (error) {
    console.error(error);
    notification.error({
      message: error.message,
    });
  }
}

function* resetPasswordStart(action: StandardAction<{ email: string; callback?: ICommonCallbackPayload }>) {
  const { payload } = action;

  if (payload) {
    const { callback } = payload;
    try {
      // const response = yield call(AuthService.resetPassword, { email });
      // MOCK DATA =======
      yield put(userResetPasswordSuccess());
      callback?.callbackOnSuccess?.();
    } catch (error) {
      yield put(userResetPasswordError(error));
      callback?.callbackOnFailure?.(error);
    }
  }
}

function* verifyToken(action: StandardAction<{ token: string; type: TokenType; callback?: CallBack }>) {
  const { payload } = action;

  if (payload) {
    const { token, type, callback } = payload;

    try {
      const response: AxiosResponse<{ message: string; email: string }> = yield call(AuthService.verifyToken, {
        token,
        type,
      });

      yield put(verifyTokenSuccess());

      if (callback) {
        callback(null, response.data);
      }
    } catch (error) {
      console.error(error);

      yield put(verifyTokenFailure(error.message));
      if (callback) {
        callback(error.message);
      }
    }
  }
}

function* getAccessTokenWithRefreshToken(action: StandardAction<{ callback?: ICommonCallbackPayload }>) {
  const { expires } = yield select(AuthSelectors.selectAuth);
  const callback = action.payload?.callback;
  if (expires && moment(expires).isAfter(moment())) return;
  try {
    const response: AxiosResponse<any> = yield call(AuthService.getNewAccessToken);

    yield put(getNewAccessTokenEnd());
    const { accessToken, expiresIn } = get(response, 'data', {});
    yield put(userSignInSuccess(accessToken, convertExpireTime(expiresIn)));
    callback?.callbackOnSuccess?.(response);
    // }
  } catch (error) {
    yield put(userSignInFailure(error));
    callback?.callbackOnFailure?.(error);
  }
}

function* watchCreateUserAccount() {
  yield takeLatest(SIGNUP.START, createUserWithEmailPassword);
}

function* watchSignInUser() {
  yield takeLatest(SIGNIN.START, signInUserWithEmailPassword);
}

function* watchSignOutUser() {
  yield takeLatest(SIGNOUT.START, signOut);
}

function* watchResetPassword() {
  yield takeEvery(RESET_PASSWORD.START, resetPasswordStart);
}

function* watchVerifyToken() {
  yield takeEvery(VERIFY_TOKEN.START, verifyToken);
}

function* watchGetAccessTokenWithRefreshToken() {
  yield takeLatest(GET_NEW_ACCESS_TOKEN.START, getAccessTokenWithRefreshToken);
}

export default function* rootSaga(): Generator {
  yield all([
    watchSignInUser(),
    watchCreateUserAccount(),
    watchSignOutUser(),
    watchResetPassword(),
    watchVerifyToken(),
    watchGetAccessTokenWithRefreshToken(),
  ]);
}
